import type { AssessmentQuestion, InstructorCue, Lesson } from "../types";
import cover from "../assets/course/customer-service/course-scaffold.svg";

const question = (
  id: string,
  prompt: string,
  options: string[],
  answer: number,
  explanation: string,
): AssessmentQuestion => ({ id, prompt, options, answer, explanation });
const cues = (
  lessonId: string,
  scripts: {
    intro: string;
    emphasis: string;
    misconception: string;
    discussion: string;
    demonstration: string;
    debrief: string;
  },
): InstructorCue[] => {
  const modes: InstructorCue["mode"][] = [
    "intro",
    "explanation",
    "worked-example",
    "activity",
    "quiz-intro",
    "summary",
  ];
  return Object.entries(scripts).map(([kind, script], index) => ({
    id: `${lessonId}-${kind}`,
    instructorId: "kojo-mensah",
    mode: modes[index],
    pose:
      index === 2
        ? "serious"
        : index === 3
          ? "thinking"
          : index === 4
            ? "point-right"
            : index === 5
              ? "open-hands"
              : "speaking",
    placement: "left-stage",
    script,
    caption: script,
    descriptiveLabel: `Mr. Kojo provides ${kind.replace("-", " ")} guidance for ${lessonId}.`,
    speakerNotes: script,
    allowDismiss: true,
    showSpeechBubble: true,
  }));
};

export const customerServiceModuleOneLessons: Lesson[] = [
  {
    id: "service-vs-experience",
    title: "Customer Service, Customer Experience and Project Success",
    duration: 35,
    outcomes: [
      "Distinguish a service interaction from the cumulative client experience",
      "Analyse functional and experiential quality across a project lifecycle",
      "Identify moments of truth and assign responsible experience owners",
      "Apply professional boundaries when client expectations conflict with scope, safety or evidence",
    ],
    executiveSummary:
      "Customer service is the help and communication provided at particular interactions. Customer experience is the client’s cumulative judgement of the whole relationship: what was promised, what occurred, how uncertainty was handled and whether the finished result matched the agreed purpose. Project success therefore includes controlled delivery and the client’s evidence-based perception of that delivery.",
    sections: [
      {
        heading: "Interaction quality is not the whole experience",
        body: "A courteous call, prompt WhatsApp reply or helpful site meeting is customer service. Client experience is formed across awareness, enquiry, briefing, proposal, payment, design, procurement, installation, snagging and after-sales. One excellent interaction cannot erase repeated uncertainty; one difficult but professionally controlled conversation need not destroy an otherwise reliable project.",
        steps: [
          "Name the interaction and its immediate purpose.",
          "Identify the expectation the client brought to it.",
          "Compare the promise with observable performance.",
          "Record the implication for the wider journey.",
        ],
      },
      {
        heading: "Functional quality and experiential quality",
        body: "Functional quality asks whether the kitchen, wardrobe or fit-out meets the agreed specification, tolerances and intended use. Experiential quality asks whether the client understood the process, could see credible evidence, knew who owned decisions and felt treated fairly. Technical quality without communication can look like neglect. Pleasant communication without controlled delivery eventually becomes reassurance without substance.",
        steps: [
          "Verify the technical result against accepted criteria.",
          "Test whether decisions and changes were visible to the client.",
          "Separate a legitimate defect from a preference outside scope.",
          "Respond with evidence and professional boundaries rather than automatic agreement.",
        ],
      },
      {
        heading: "Moments of truth and shared responsibility",
        body: "A moment of truth is a touchpoint that strongly changes confidence: the first site visit, deposit request, delayed delivery notice, installation day, snag response or handover. Sales, design, procurement, accounts, supervisors, installers and owners all shape experience. Project success is multidimensional: it can include the agreed technical result, scope and cost control, time performance, quality, client confidence, clear communication, safe and professional delivery, and documented completion. These dimensions can conflict. Rushing installation may protect a date but damage quality; pursuing exceptional finish quality without explaining a delay may protect the product but destroy confidence. Professional judgement makes the trade-off visible, protects non-negotiable safety and quality, and obtains the right decision rather than optimising one measure in isolation. If responsibility is unclear, the client becomes the coordinator.",
        steps: [
          "List the technical, scope, cost, time, quality, confidence, safety and completion dimensions affected by the decision.",
          "Identify which requirements are non-negotiable and where a genuine trade-off exists.",
          "Make the consequence of each realistic option visible to the client and project team.",
          "Confirm the responsible role, decision route and completion record for the chosen option.",
        ],
      },
    ],
    workedExample: {
      title: "Beautiful kitchen, damaging silence",
      scenario:
        "An Accra homeowner pays a staged deposit for a fitted kitchen. Cabinet construction is accurate and installation quality is strong, but the firm gives no update for twelve days after a supplier changes the worktop delivery date. The client visits the workshop unannounced, assumes the deposit has been diverted and tells family members the contractor is unreliable.",
      calculation: [
        "Functional quality: cabinet work meets the approved drawings.",
        "Experiential failure: the promised update rhythm was not maintained.",
        "Moment of truth: unexplained supplier delay after a major payment.",
        "Controlled alternative: notify early, show supplier evidence, explain programme impact, offer authorised options and confirm the next update.",
      ],
      decision:
        "Acknowledge the communication failure without admitting a defect that does not exist. Provide evidence, a revised sequence and a named update owner. Technical success and client confidence must be managed together.",
    },
    practicalExamples: [
      "Think Before You Continue: installation is one day behind. Option A rushes fitting and reduces final quality checks; Option B protects checks but advises a revised handover; Option C stays silent and tries to recover later. Choose before comparing how each option affects the different dimensions of project success.",
      "Map who owns the client experience when sales promises a date that procurement and production have not validated.",
    ],
    commonMistakes: [
      "Equating friendliness with a complete client experience",
      "Assuming technically correct work automatically feels successful to the client",
      "Promising a remedy before checking scope, evidence and authority",
      "Treating the phrase “the customer is always right” as a substitute for professional judgement",
    ],
    bestPractices: [
      "Define service standards at high-anxiety touchpoints",
      "Confirm promises only after operational validation",
      "Use drawings, photographs, programmes and approvals to make performance visible",
      "State boundaries respectfully when a request conflicts with contract, safety or quality",
    ],
    visual: {
      type: "comparison",
      title: "Customer Service versus Customer Experience",
      purpose:
        "Distinguish individual interactions from the cumulative project judgement.",
      description:
        "Two connected lanes. The upper lane shows service interactions such as enquiry reply, site meeting and snag response. The lower lane shows the full lifecycle from expectation through delivery and after-sales. A third band compares functional quality with experiential quality, while highlighted moments of truth show where confidence changes.",
      altText:
        "A lifecycle comparison showing individual service interactions within the wider client experience, with functional and experiential quality and moments of truth.",
      aspectRatio: "16:9",
      source: cover,
    },
    reflection: [
      "Where has your business delivered technically acceptable work but still created avoidable client anxiety?",
      "Which promise currently leaves your team without the authority or evidence required to keep it?",
    ],
    exercise:
      "Analyse a residential kitchen project with good workmanship and poor updates. Identify four moments of truth, the client expectation at each, the accountable role, evidence required, professional boundary and improved response.",
    summary:
      "Project success requires both controlled functional delivery and a client experience built through clear expectations, evidence, ownership and fair professional boundaries.",
    takeaways: [
      "Customer service occurs at touchpoints; client experience accumulates across the lifecycle.",
      "Functional quality and experiential quality are different but interdependent.",
      "Every critical touchpoint needs an owner, evidence standard and escalation path.",
    ],
    questions: [
      question(
        "service-vs-experience-q1",
        "A client praises the receptionist but complains that nobody explained a two-week installation delay. What is the strongest diagnosis?",
        [
          "Customer service and client experience were both excellent",
          "One service interaction was positive, but the cumulative experience failed at a moment of truth",
          "The technical team alone owns the experience",
          "The client is unreasonable because the receptionist was polite",
        ],
        1,
        "A pleasant interaction is service quality at one touchpoint. The unexplained delay affects the cumulative experience and trust in project control.",
      ),
      question(
        "service-vs-experience-q2",
        "Cabinet doors meet the approved tolerance, but the client expected a different colour seen on an unapproved social-media image. What should the firm do first?",
        [
          "Replace everything immediately",
          "Dismiss the client’s concern",
          "Compare approved evidence with the finished work, explain the distinction and explore authorised options",
          "Promise a free upgrade before reviewing scope",
        ],
        2,
        "Professional service validates the concern while using approved drawings or samples to distinguish a defect from a changed preference.",
      ),
      question(
        "service-vs-experience-q3",
        "The client is about to pay the first substantial deposit. Which action best manages this moment of truth?",
        [
          "Send the payment number promptly and explain mobilisation after funds arrive",
          "Explain the payment trigger, what mobilisation covers, the receipt process, next action and responsible contact",
          "Provide a detailed contract again but leave the mobilisation sequence for the project team",
          "Reassure the client that established contractors normally require deposits",
        ],
        1,
        "The selected response balances commercial need with client vulnerability by making the trigger, use, record and next step clear. A contract alone may be complete but does not necessarily explain what the client should expect immediately after payment.",
      ),
      question(
        "service-vs-experience-q4",
        "Why can technically correct work still create a poor experience?",
        [
          "Clients never value workmanship",
          "Clients judge only price",
          "Unmanaged expectations, silence and unclear ownership can make correct delivery appear uncontrolled",
          "Technical quality is irrelevant",
        ],
        2,
        "Clients judge both the result and the process through which uncertainty, decisions and commitments were managed.",
      ),
      question(
        "service-vs-experience-q5",
        "Who owns client experience in a design-and-build company?",
        [
          "Only customer service staff",
          "Only the managing director",
          "Every role that creates or fulfils client expectations, with named accountability at each touchpoint",
          "The client",
        ],
        2,
        "Experience crosses departments. Shared contribution still requires explicit ownership at each critical touchpoint.",
      ),
    ],
    speakerNotes:
      "Open by asking learners to describe a technically successful project that felt unsuccessful to the client. Keep functional quality separate from experiential quality. Challenge “the customer is always right”: the professional duty is to listen, verify, explain and act within scope, authority, safety and evidence.",
    instructorCues: cues("service-vs-experience", {
      intro:
        "A warm greeting matters, but it is only one frame in a much longer project story.",
      emphasis:
        "Project success has several dimensions. Before choosing speed, finish quality or client convenience, protect safety and agreed acceptance criteria, then make the remaining trade-off and its consequence explicit.",
      misconception:
        "Watch for “the customer is always right.” Respect does not require unsafe work, unapproved scope or false admission.",
      discussion:
        "Ask for an example of good workmanship damaged by silence. What exactly changed the client’s judgement?",
      demonstration:
        "Pause on the late installation choice before revealing the answer. Trace what each option protects and damages across time, quality, confidence, safety and documented completion.",
      debrief:
        "Close by assigning owners and evidence to four moments of truth in the kitchen case.",
    }),
    references: [
      "ISO 9001 quality-management principles on customer focus",
      "RIBA business, clients and services guidance",
      "PMI stakeholder and expectation-management guidance",
    ],
    version: "1.0.0",
    status: "review",
    estimatedMinutes: 35,
    prerequisites: ["Basic business communication"],
    skillsAcquired: [
      "Distinguish service and experience",
      "Analyse functional and experiential quality",
      "Identify moments of truth",
    ],
    sourceIds: ["iso-9001-customer-focus", "riba-clients", "pmi-expectations"],
  },
  {
    id: "economics-of-trust",
    title: "The Economics of Trust",
    duration: 40,
    outcomes: [
      "Explain trust as accumulated evidence of competence, reliability and honesty",
      "Analyse how trust changes payment, approval and dispute behaviour",
      "Evaluate how communication choices build or consume trust capital",
      "Choose a commercially responsible response to delivery uncertainty",
    ],
    executiveSummary:
      "Trust is a project and commercial asset created by repeated evidence that the firm is competent, reliable, honest, transparent and consistent. Trust capital reduces the client’s need to verify every action. Broken commitments consume that capital and increase friction, supervision and price sensitivity.",
    sections: [
      {
        heading: "How trust capital is built",
        body: "Trust is not a personality trait or marketing claim. It is the client’s evidence-based willingness to accept vulnerability—paying a deposit, approving a design or allowing work in an occupied property—because past behaviour suggests the firm will perform responsibly. ‘Trust Capital’ is a conceptual model used in this course to reason about accumulated confidence. It is not an accounting asset, cash balance or directly measurable financial amount. The metaphor helps a professional ask whether recent behaviour gives the client more or less reason to rely on the firm.",
        steps: [
          "Competence: demonstrate the ability to do the work.",
          "Reliability: do what was agreed when it was agreed.",
          "Honesty: distinguish fact, forecast and uncertainty.",
          "Transparency: make relevant evidence and implications visible.",
          "Consistency: repeat the standard across people and stages.",
        ],
      },
      {
        heading: "The commercial effects of trust",
        body: "Trust can reduce avoidable friction around milestone payments, approvals and reasonable variation decisions because the client can understand the basis of the request. Low trust can produce repeated checking, site interference, delayed approvals, defensive withholding, rework disputes and stronger price resistance. Professional trust is different from blind trust: it welcomes proportionate verification, accurate records and questions. Documentation preserves what was agreed; transparency explains relevant facts and uncertainty; verification tests whether the claim is supported. Trust does not remove these controls—it allows them to work without suspicion or needless duplication.",
        steps: [
          "Connect each commitment to evidence.",
          "Measure whether updates arrive when promised.",
          "Record approvals so neither party relies on memory.",
          "Treat bad news as a decision point, not a communication failure to hide.",
        ],
      },
      {
        heading: "Deposits, delays and the trust account",
        body: "Each kept promise makes a small deposit into trust capital. A missed promise makes a withdrawal; concealment makes a larger one because it damages both delivery and honesty. Recovery requires acknowledgement, evidence, realistic options and observable follow-through—not a new unsupported promise.",
      },
    ],
    workedExample: {
      title: "The same supplier delay, two commercial outcomes",
      scenario:
        "Two Tema office-fit-out contractors learn that imported lighting will arrive ten days late. Contractor A stays silent until installation day. Contractor B informs the facility manager immediately, attaches the supplier notice, shows the programme effect and offers temporary local fittings or resequenced ceiling work.",
      calculation: [
        "Contractor A: surprise, questioned competence, delayed milestone certification and disputed acceleration cost.",
        "Contractor B: early decision window, evidence of cause, documented option and preserved approval path.",
        "Both face the same external delay; their trust consequences differ because uncertainty is managed differently.",
      ],
      decision:
        "Communicate verified bad news early enough for the client to make a meaningful decision. Do not blame the supplier or promise a recovery date that procurement has not validated.",
    },
    practicalExamples: [
      "Think Before You Continue: a long-standing client says, ‘I trust you; no need to send the variation.’ Decide whether to proceed, request a brief written approval, or delay all work for a formal contract amendment. Then compare relationship, scope and payment risk.",
      "Role-play a supplier delay update where the client demands an impossible original date.",
    ],
    commonMistakes: [
      "Hiding bad news until certainty feels comfortable",
      "Using repeated reassurance instead of evidence",
      "Believing trust means the client should waive documentation",
      "Making a heroic promise to repair trust and then missing it",
    ],
    bestPractices: [
      "Keep a commitment register with owner and due date",
      "Separate confirmed facts from forecasts and assumptions",
      "Notify material variance while options still exist",
      "Close the loop visibly after every recovery promise",
    ],
    visual: {
      type: "process-flow",
      title: "Trust Capital Cycle",
      purpose:
        "Show how operational behaviour changes commercial confidence over time.",
      description:
        "A circular model links competence, reliable commitments, transparent evidence and consistent follow-through to trust capital. From trust capital, arrows lead to faster approvals, lower anxiety and repeat business. A red withdrawal path shows concealment, missed commitments and inconsistent records leading to scrutiny, disputes and price sensitivity.",
      altText:
        "Trust capital grows through competence, reliability, honesty, transparency, evidence and consistency; broken commitments and concealment reduce it and increase commercial friction.",
      aspectRatio: "16:9",
      source: cover,
    },
    reflection: [
      "Which recurring commitment does your company miss or renegotiate most often?",
      "What evidence could reduce client anxiety before the next deposit or milestone payment?",
    ],
    exercise:
      "Prepare an evidence-based delay response for the Tema fit-out. State verified fact, client impact, two realistic options, commercial implications, decision required, accountable owner and next update time.",
    summary:
      "Trust capital converts reliable behaviour into commercial confidence. It is accumulated slowly through evidence and consumed quickly by concealment or unsupported promises.",
    takeaways: [
      "Trust is operational evidence, not charm.",
      "Early truthful communication preserves options and approval paths.",
      "Recovery succeeds through observable follow-through, not stronger reassurance.",
    ],
    questions: [
      question(
        "economics-of-trust-q1",
        "A contractor has missed one update but the revised installation date is still achievable. What should happen first?",
        [
          "Send an apology and promise that installation remains on schedule",
          "Verify current status, acknowledge the missed update, explain remaining uncertainty and set the next dependable communication point",
          "Wait until installation is complete so the client receives one final accurate report",
          "Offer a small discount immediately to demonstrate goodwill",
        ],
        1,
        "The selected response repairs the reliability failure without replacing it with another unsupported promise. An apology or discount may help the relationship, but neither resolves the missing facts and dependable next commitment.",
      ),
      question(
        "economics-of-trust-q2",
        "Why might high trust improve variation approval speed?",
        [
          "The client stops caring about price",
          "Credible records make scope, impact and authority easier to evaluate",
          "The contractor can skip consent",
          "Trust converts every request into entitlement",
        ],
        1,
        "Trust does not replace control. It makes evidence and recommendations more credible, reducing avoidable verification friction.",
      ),
      question(
        "economics-of-trust-q3",
        "A supplier delay is not yet fully resolved. What is the strongest communication decision?",
        [
          "Wait until every fact is certain",
          "Blame the supplier immediately",
          "Share verified facts, identify uncertainty, explain potential impact and commit to the next update",
          "Promise the original date",
        ],
        2,
        "Early qualified information preserves decision time without presenting forecasts as facts.",
      ),
      question(
        "economics-of-trust-q4",
        "What most seriously consumes trust capital?",
        [
          "A documented risk outside the contractor’s control",
          "A difficult but honest discussion",
          "A missed commitment concealed until the client discovers it",
          "A request for written approval",
        ],
        2,
        "Concealment damages both performance confidence and belief in the firm’s honesty.",
      ),
      question(
        "economics-of-trust-q5",
        "Which statement about trust is professionally sound?",
        [
          "Trusted firms do not need contracts",
          "Trust reduces friction but should remain supported by evidence and controls",
          "Trust is created mainly by discounts",
          "Trust means accepting every client demand",
        ],
        1,
        "Professional trust and formal controls reinforce each other; neither should replace the other.",
      ),
    ],
    speakerNotes:
      "Draw a trust account on the board. Ask learners to classify behaviours as deposits or withdrawals and explain the commercial consequence. Avoid unsupported claims about percentages or revenue uplift.",
    instructorCues: cues("economics-of-trust", {
      intro:
        "Trust Capital is our reasoning metaphor for accumulated confidence. Do not put it on a balance sheet; use it to ask whether today’s behaviour gives the client more or less reason to rely on us.",
      emphasis:
        "Competence, reliability, honesty, transparency, consistency and evidence must work together.",
      misconception:
        "Correct this carefully: trust is not blind acceptance, and verification is not automatically distrust. A trusted professional welcomes proportionate checks and keeps records that make those checks efficient.",
      discussion:
        "Which broken promise creates the most expensive downstream friction in your projects?",
      demonstration:
        "Before showing Contractor B’s response, ask the learner to choose what to disclose while facts are incomplete. Then separate verified fact, forecast, uncertainty, option and next commitment.",
      debrief:
        "Ask learners to rewrite one unsupported recovery promise as a verified commitment with an owner and update time.",
    }),
    references: [
      "ISO 10002 complaints-handling principles",
      "PMI stakeholder and expectation-management guidance",
      "RIBA business, clients and services guidance",
    ],
    version: "1.0.0",
    status: "review",
    estimatedMinutes: 40,
    prerequisites: [
      "Customer Service, Customer Experience and Project Success",
    ],
    skillsAcquired: [
      "Evaluate trust capital",
      "Communicate uncertainty commercially",
      "Analyse trust consequences",
    ],
    sourceIds: ["iso-10002", "pmi-expectations", "riba-clients"],
  },
  {
    id: "understanding-clients",
    title: "Understanding Construction and Interior Clients",
    duration: 40,
    outcomes: [
      "Map decision, payment, use and influence roles in a client system",
      "Distinguish stated, functional, emotional and commercial needs",
      "Build an evidence-based client profile without stereotyping",
      "Identify authority, constraints, risk tolerance and success criteria",
    ],
    executiveSummary:
      "In project work, “the client” is often a system of people rather than one payer. Sound client understanding identifies who decides, pays, uses, influences, approves and experiences disruption, then converts different needs and constraints into an agreed decision process.",
    sections: [
      {
        heading: "The client is a stakeholder system",
        body: "A homeowner may pay while a spouse approves appearance and an elderly parent uses the kitchen. A commercial organisation may involve procurement, a facility manager, finance, users, an architect and an executive sponsor. Treating the loudest contact as the sole authority creates approval and payment risk.",
        steps: [
          "Identify decision maker and delegated limits.",
          "Identify payer and payment process.",
          "Identify users and operational requirements.",
          "Identify technical advisers and influencers.",
          "Confirm who can approve scope, cost, design and time.",
        ],
      },
      {
        heading: "Needs beneath the initial request",
        body: "A stated need is what the client asks for. Functional needs describe what the result must enable. Emotional needs concern confidence, dignity, privacy, control and fear. A homeowner may fear losing money, poor workmanship, abandonment, hidden cost or a result that differs from the design; a business user may fear disruption, embarrassment before colleagues or loss of operating time. Commercial needs concern revenue, continuity, compliance or operating cost. Authority can be perfectly clear while experience still fails—for example, a homeowner authorises dusty demolition but the contractor gives no daily warning, leaves private rooms exposed and speaks dismissively to occupants. The instruction is valid; the delivery still mishandles dignity, privacy and disruption. Constraints include budget, access, time and existing conditions; preferences are negotiable choices; non-negotiables are acceptance boundaries.",
        steps: [
          "Record the stated request without treating it as the full brief.",
          "Ask what successful use looks like.",
          "Surface anxieties and disruption concerns.",
          "Test budget and timeline reality respectfully.",
          "Separate preferences from non-negotiable criteria.",
        ],
      },
      {
        heading: "Profile evidence, not stereotypes",
        body: "A useful profile predicts the decisions and communication controls the project requires. It does not label a client ‘difficult,’ ‘rich,’ ‘indecisive’ or ‘typical.’ Behaviour may reflect unclear authority, prior loss, competing users or unspoken constraints. Collect only information relevant to delivery and explain why it is needed where appropriate. Avoid unnecessary sensitive details, restrict access to people who require the information, protect documents and contact data, and do not circulate profiles casually through personal WhatsApp groups. Correct inaccurate information. Record professional observations—such as ‘requires written cost comparison before approval’—rather than insulting personality judgements.",
        steps: [
          "Capture the communication preference, approval process, budget reality and success criteria needed for the project.",
          "Ask about privacy, disruption, control and anxiety only to the extent that delivery requires it.",
          "Separate observed behaviour and confirmed needs from personal interpretation.",
          "Limit access, protect client documents and update information shown to be inaccurate.",
          "Review the profile with the client or authorised contact so it supports decisions rather than stereotypes.",
        ],
      },
    ],
    workedExample: {
      title: "One renovation, four versions of success",
      scenario:
        "A Kumasi family renovates a ground-floor interior. The daughter abroad funds the work, her father signs site instructions, her mother is the main user and a local architect reviews finishes. The father verbally requests a premium ceiling change, but the daughter has set a fixed budget and the architect has not approved the detail.",
      calculation: [
        "Payer: daughter; commercial authority must be confirmed.",
        "Site representative: father; delegated instruction limit is unclear.",
        "Primary user: mother; access and functional needs require direct confirmation.",
        "Technical influencer: architect; finish approval route must be defined.",
        "Risk: verbal change may conflict with budget, design and authority.",
      ],
      decision:
        "Pause the change, document the request and route it through the agreed approval map. Clarify functional need, cost, programme and authorised sign-off without disrespecting the family representative.",
    },
    practicalExamples: [
      "Think Before You Continue: a homeowner has clearly authorised demolition, but the team exposes private rooms, gives no dust warning and dismisses an elderly occupant’s concern. Decide what must change even though authority is not disputed.",
      "Convert “I want a modern wardrobe” into stated, functional, emotional and constraint questions.",
    ],
    commonMistakes: [
      "Assuming the payer is the sole decision maker",
      "Treating a family or organisational representative as having unlimited authority",
      "Stereotyping clients instead of recording evidence",
      "Failing to distinguish preferences from acceptance criteria",
      "Ignoring disruption experienced by users who did not sign the contract",
    ],
    bestPractices: [
      "Confirm an authority matrix before design approval",
      "Record communication preference and required response time",
      "Test budget and timeline against scope before promising",
      "Write observable success criteria into the brief",
      "Update the profile when stakeholders or constraints change",
    ],
    visual: {
      type: "diagram",
      title: "Client Stakeholder and Authority Map",
      purpose:
        "Make decision rights and client needs visible before commitments are accepted.",
      description:
        "A hub-and-spoke map places the project at the centre and shows decision maker, payer, user, influencer, technical adviser and representative. Each role carries fields for authority limit, approval type, communication channel, need, anxiety and evidence required. Overlap indicators show where one person holds multiple roles.",
      altText:
        "Stakeholder map linking decision maker, payer, users, influencers, advisers and representatives to authority, needs, communication and approvals.",
      aspectRatio: "16:9",
      source: cover,
    },
    reflection: [
      "Whose needs are routinely discovered too late in your projects?",
      "Which current project relies on verbal authority that has never been defined?",
    ],
    exercise:
      "Create a Client Profile for the Kumasi renovation or a live project. Include stakeholder roles, authority, communication preference, stated/functional/emotional/commercial needs, constraints, preferences, non-negotiables, budget reality, timeline, risk tolerance and observable success criteria.",
    summary:
      "Professional client understanding replaces assumptions with a stakeholder, authority and needs profile that supports fair decisions and controlled approvals.",
    takeaways: [
      "The client may be a system of payers, users, decision makers and influencers.",
      "Needs operate at stated, functional, emotional and commercial levels.",
      "Authority and success criteria must be confirmed, not inferred.",
    ],
    questions: [
      question(
        "understanding-clients-q1",
        "A site representative requests additional cabinetry but the contract does not state their spending authority. What is the strongest response?",
        [
          "Proceed because they are on site",
          "Reject the request rudely",
          "Document the request and confirm scope, impact and authorised approval route before proceeding",
          "Ask the artisan to decide",
        ],
        2,
        "Respecting a representative does not mean assuming unlimited authority. Verification prevents later cost and scope disputes.",
      ),
      question(
        "understanding-clients-q2",
        "Which question best reveals a functional need behind “I want an open kitchen”?",
        [
          "Which celebrity kitchen do you like?",
          "What activities, users and movement must the kitchen support each day?",
          "Can you pay the deposit today?",
          "Do you trust us?",
        ],
        1,
        "Functional questions connect form to actual use rather than treating an aesthetic phrase as a complete brief.",
      ),
      question(
        "understanding-clients-q3",
        "A team records a homeowner as ‘difficult’ because she requests daily photographs after a previous contractor abandoned her project. What is the most useful profile entry?",
        [
          "High-maintenance client; minimise contact to prevent more requests",
          "Prior abandonment concern; agree a proportionate photo-update rhythm and review it after mobilisation",
          "Anxious personality; circulate the warning to every artisan in the WhatsApp group",
          "Daily photographs required indefinitely, even when they expose unrelated private areas",
        ],
        1,
        "The selected entry turns relevant history into a delivery control without insulting labels or unnecessary circulation. Indefinite reporting or sharing private images would ignore proportionality and privacy.",
      ),
      question(
        "understanding-clients-q4",
        "In a commercial fit-out, who should approve a design change?",
        [
          "Whoever replies first",
          "The person with documented authority for that class of design, cost and programme decision",
          "Only the end user",
          "The supplier",
        ],
        1,
        "Approval must follow the agreed authority structure; influence and use do not automatically confer contractual authority.",
      ),
      question(
        "understanding-clients-q5",
        "Which is an observable success criterion?",
        [
          "Make it premium",
          "The reception desk accommodates two users, concealed cabling and the approved accessibility clearance",
          "The client feels impressed",
          "Use the best materials",
        ],
        1,
        "Observable criteria can be reviewed against evidence; vague aspirations require further clarification.",
      ),
    ],
    speakerNotes:
      "Do not let the stakeholder map become a political ranking. It is a decision-control tool. In Ghanaian family projects, discuss representatives respectfully while still confirming authority and budget. Ask learners to replace labels with observable facts.",
    instructorCues: cues("understanding-clients", {
      intro:
        "Before we manage expectations, we must know whose expectations and whose authority matter.",
      emphasis:
        "Authority tells us who may decide; it does not tell us who bears disruption, needs privacy or fears financial loss. A complete profile attends to both decision control and human experience.",
      misconception:
        "A person who is vocal, present or related to the payer does not automatically hold unlimited approval authority.",
      discussion:
        "Authority is clear in the demolition scenario. Ask the learner what still failed: warning, dignity, privacy, disruption planning or all four—and what the team should change first.",
      demonstration:
        "Build the Kumasi family authority map live and show where a verbal ceiling change becomes risky.",
      debrief:
        "Review each Client Profile: is every item relevant, professionally worded, accurate and available only to people who need it? Replace personality labels with observable needs or decision conditions.",
    }),
    references: [
      "PMI stakeholder-management guidance",
      "RIBA client and project-brief guidance",
      "Ghana Data Protection Commission guidance on responsible personal-data handling",
    ],
    version: "1.0.0",
    status: "review",
    estimatedMinutes: 40,
    prerequisites: ["The Economics of Trust"],
    skillsAcquired: [
      "Map client authority",
      "Analyse layered needs",
      "Build a client profile",
    ],
    sourceIds: ["pmi-stakeholders", "riba-brief", "ghana-data-protection"],
  },
  {
    id: "client-journey",
    title: "Mapping the Client Journey",
    duration: 50,
    outcomes: [
      "Map the complete design-and-build client journey beyond construction activities",
      "Define expectations, touchpoints, ownership and evidence at each stage",
      "Identify friction points, moments of truth and recovery opportunities",
      "Produce an operational Client Journey Map linked to the Client Profile",
    ],
    executiveSummary:
      "A client journey map is an operating control, not a decorative timeline. It traces the client’s expectations and interactions from awareness to repeat business, assigns ownership and evidence, and exposes where departmental hand-offs create uncertainty or failure.",
    sections: [
      {
        heading: "Map the complete lifecycle",
        body: "The journey begins before contract and continues after handover. A useful map includes Awareness, Enquiry, Qualification, Consultation, Brief, Proposal, Decision, Contract, Deposit, Design/Planning, Procurement, Production, Site Preparation, Installation/Construction, Inspection, Snagging, Handover, After-Sales, Feedback and Referral/Repeat Business. The guided lesson does not require every field across all twenty stages. First understand the full lifecycle, then select five to seven critical stages from the supplied case and map those deeply. The complete relevant journey becomes the independent Module 1 output.",
        steps: [
          "Keep client stages distinct from the internal construction programme.",
          "Record what the client is trying to understand or decide at each stage.",
          "Connect every stage to the relevant project control.",
          "Include exit, pause and recovery routes where the journey may not proceed normally.",
        ],
      },
      {
        heading: "Turn touchpoints into standards",
        body: "A touchpoint is any meaningful contact with the firm, its people, systems, documents or work. For each touchpoint define the expected standard, responsible role, evidence, response time or quality criterion and escalation route. The firm should not depend on one employee remembering what happens next.",
        steps: [
          "List calls, visits, messages, documents, payments and physical encounters.",
          "Identify the client expectation at each.",
          "Assign one accountable owner across departmental hand-offs.",
          "Name the durable evidence produced.",
          "Define failure risk and minimum service standard.",
        ],
      },
      {
        heading: "Find friction, truth and recovery",
        body: "Friction consumes client effort or creates uncertainty. Moments of truth carry disproportionate confidence impact, such as deposit, first delay, installation start or snag response. A recovery opportunity is the point where early acknowledgement and controlled action can prevent a local failure from defining the entire relationship.",
        steps: [
          "Mark high-anxiety or irreversible decisions.",
          "Identify hand-offs where information may be lost.",
          "Locate stages without an owner or evidence.",
          "Design a recovery trigger, authority and response standard.",
          "Review the map using an actual Client Profile.",
        ],
      },
    ],
    workedExample: {
      title: "The office fit-out journey that ended at handover",
      scenario:
        "A growing Accra firm completes a commercial office fit-out. Sales handled enquiry and proposal, accounts collected the deposit, a project manager led delivery, and installers closed snags. No one explained the hand-off between teams. After handover, the facility manager receives no warranty contacts, feedback request or follow-up. The work is acceptable, but the client does not invite the firm to tender for the next floor.",
      calculation: [
        "Friction: the client repeats information at each departmental hand-off.",
        "Moment of truth: deposit is collected without a mobilisation communication.",
        "Moment of truth: installation begins without an access and disruption briefing.",
        "Failure: handover closes construction but not the client relationship.",
        "Recovery opportunity: named after-sales contact, warranty record, scheduled follow-up and feedback closure.",
      ],
      decision:
        "Create one lifecycle owner map with hand-off evidence, a formal handover-to-after-sales transition and a consent-aware route to feedback and future opportunity.",
    },
    practicalExamples: [
      "Think Before You Continue: from the office fit-out case, choose five to seven stages with the greatest client risk. Explain why those stages deserve deeper mapping before viewing the worked reasoning.",
      "Audit one current journey stage that depends entirely on WhatsApp memory.",
    ],
    commonMistakes: [
      "Mapping only internal construction tasks rather than the client journey",
      "Listing touchpoints without expectations, owners or standards",
      "Ending the journey at practical completion",
      "Ignoring failed enquiries and recovery paths",
      "Assuming each department knows what the previous one promised",
    ],
    bestPractices: [
      "Use one accountable owner at every stage and hand-off",
      "Link touchpoints to named documents or evidence",
      "Highlight moments of truth for proactive communication",
      "Include after-sales, feedback and ethical referral processes",
      "Review the map against real client profiles and failure records",
    ],
    visual: {
      type: "timeline",
      title: "End-to-End Client Journey Map",
      purpose:
        "Provide an implementation-ready operating view of expectations, ownership, evidence and risk across the full lifecycle.",
      description:
        "A horizontal 20-stage timeline from Awareness to Referral/Repeat Business. Each stage expands into rows for client expectation, touchpoint, responsible role, evidence/document, risk, moment-of-truth flag, recovery trigger and service standard. Department swim lanes reveal hand-offs between sales, design, accounts, procurement, production, site and after-sales.",
      altText:
        "Twenty-stage client journey with swim lanes and rows for expectations, touchpoints, owners, evidence, risks, moments of truth, recovery and service standards.",
      aspectRatio: "16:9",
      source: cover,
    },
    reflection: [
      "Where does your current journey create the longest period of unexplained silence?",
      "Which touchpoint depends entirely on one employee’s memory or personal WhatsApp account?",
    ],
    exercise:
      "Guided practice: (1) review all 20 lifecycle stages; (2) select approximately five to seven critical stages from the office fit-out case; (3) deeply map the expectation, touchpoint, responsible role, documentation, risk, moment of truth, recovery opportunity and service standard for those stages. Independent Module 1 output: extend the same method to every applicable stage in a real or simulated design-and-build journey and validate the completed map against the Client Profile from Lesson 3.",
    summary:
      "A journey map becomes operational when it connects client expectations to accountable roles, durable evidence, service standards and recovery triggers across departmental boundaries.",
    takeaways: [
      "The client journey is wider than the construction programme.",
      "A touchpoint without ownership and evidence is a reliability risk.",
      "After-sales and recovery belong inside the designed journey.",
    ],
    questions: [
      question(
        "client-journey-q1",
        "A journey map lists all project stages but staff still ask the client repeatedly who approves changes. What is missing?",
        [
          "More detailed construction tasks at every stage",
          "A single relationship manager who approves on the client’s behalf",
          "The decision touchpoints, client authority route, responsible internal role and confirmation record",
          "A shorter map ending when the contract is signed",
        ],
        2,
        "Stages alone describe sequence, not decision control. The selected addition connects the client’s authority route with internal responsibility and a durable hand-off record; a relationship manager cannot assume client authority.",
      ),
      question(
        "client-journey-q2",
        "Why should Deposit be treated as a moment of truth?",
        [
          "It is always the largest payment",
          "The client accepts financial vulnerability and needs evidence of what happens next",
          "Accounts owns the entire experience",
          "Deposits remove project risk",
        ],
        1,
        "Payment increases perceived exposure. A clear receipt, mobilisation plan and next action demonstrate control.",
      ),
      question(
        "client-journey-q3",
        "A hand-off from sales to design repeatedly loses client preferences. What is the strongest redesign?",
        [
          "Tell sales to remember better",
          "Ask the client to repeat everything",
          "Create a confirmed brief, named hand-off owner and acceptance check before design begins",
          "Remove the design stage",
        ],
        2,
        "A durable hand-off control reduces dependence on memory and makes responsibility visible.",
      ),
      question(
        "client-journey-q4",
        "Where should the journey end?",
        [
          "At deposit",
          "At installation",
          "At handover",
          "After after-sales, feedback and an appropriate path to repeat business or referral",
        ],
        3,
        "The relationship continues through support, learning and ethical future engagement.",
      ),
      question(
        "client-journey-q5",
        "What is a recovery opportunity?",
        [
          "A reason to hide failure",
          "A point where timely acknowledgement and controlled action can stop a local failure defining the relationship",
          "An automatic discount",
          "A final legal dispute",
        ],
        1,
        "Recovery opportunities are designed intervention points with authority, evidence and follow-through.",
      ),
    ],
    speakerNotes:
      "Build the map in layers rather than reading twenty stages aloud. Start with the client’s question at each stage, then add owner and evidence. Use the commercial fit-out to expose departmental hand-offs. The final exercise must integrate the Client Profile from Lesson 3.",
    instructorCues: cues("client-journey", {
      intro:
        "A project programme tells us how work moves. A client journey tells us how confidence and decisions move.",
      emphasis:
        "Depth matters more than filling boxes quickly. During guided practice, map five to seven high-risk stages well; then extend the method independently across the relevant journey.",
      misconception:
        "A journey map that ends at installation or lists only construction tasks is incomplete.",
      discussion:
        "At which hand-off does your client most often repeat information or chase an update?",
      demonstration:
        "Ask the learner to select the five to seven critical fit-out stages before revealing the model selection. Then map one hand-off fully from expectation through recovery trigger.",
      debrief:
        "Test each map against a specific Client Profile and ask where recovery would begin after a missed promise.",
    }),
    references: [
      "RIBA client and project lifecycle guidance",
      "PMI stakeholder and communications-management guidance",
      "ISO 9001 customer-focus and process-approach principles",
      "ISO 10002 complaints-handling principles",
    ],
    version: "1.0.0",
    status: "review",
    estimatedMinutes: 50,
    prerequisites: ["Understanding Construction and Interior Clients"],
    skillsAcquired: [
      "Map a client journey",
      "Design touchpoint controls",
      "Identify friction and recovery opportunities",
    ],
    sourceIds: [
      "riba-lifecycle",
      "pmi-communications",
      "iso-9001-process",
      "iso-10002",
    ],
  },
];
